// Armado de links `wa.me` para avisar al cliente desde el POS.
//
// Vivía dentro de `EnviarSeguimientoModal.vue`. Se sacó acá porque los avisos
// de retiro y de envío (RF-4 y RF-5 de SPEC_SUITE_BASE_COMERCIO_v0.1.md) van a
// necesitar exactamente la misma normalización, y duplicarla es garantizar que
// las dos copias se separen.
//
// Los teléfonos guardados vienen mezclados —verificado sobre la base real:
// `clientes.telefono` guarda `0971722168` (local) y `ventas.telefono_celular`
// tiene tanto `0981000111` como `+595981122334`. `wa.me` exige formato
// internacional sin `+` ni cero de salida, así que hay que normalizar por país.

// `local` recibe sólo los dígitos nacionales (ya sin código de país) y
// devuelve la parte que espera WhatsApp.
//
// `movil` es la regla que decide si el número puede recibir WhatsApp. Sólo
// está definida para Paraguay: es el único país del que tenemos datos reales
// en la base para validarla. Donde es `null` se valida sólo el largo, que es
// exactamente lo que se hacía antes — no se endurece a ciegas un país cuyo
// plan de numeración no verificamos.
export const PAISES = [
    {
        codigo: '595',
        nombre: 'Paraguay',
        bandera: '🇵🇾',
        placeholder: '0981 123456',
        local: (d) => d.replace(/^0+/, ''),
        largo: [8, 10],
        // Los celulares paraguayos son `09xx xxxxxx`: sin el cero de salida
        // quedan 9 dígitos que arrancan en 9. Los prefijos `02x`/`03x`/`04x`
        // son líneas bajas y WhatsApp no llega — ver `motivoNoMovil`.
        movil: /^9\d{8}$/,
        motivoNoMovil: 'Parece una línea baja. WhatsApp sólo llega a celulares (09xx).'
    },
    {
        // Argentina es el caso especial: WhatsApp exige un `9` entre el código
        // de país y el área para celulares (54 9 11 ...), y NO lleva el `15`.
        // Por eso el placeholder pide área + número pelado.
        codigo: '54',
        nombre: 'Argentina',
        bandera: '🇦🇷',
        placeholder: '11 2345-6789',
        local: (d) => {
            const sinCero = d.replace(/^0+/, '')
            return sinCero.startsWith('9') ? sinCero : `9${sinCero}`
        },
        largo: [10, 12],
        movil: null,
        motivoNoMovil: ''
    },
    {
        codigo: '55',
        nombre: 'Brasil',
        bandera: '🇧🇷',
        placeholder: '11 91234-5678',
        local: (d) => d.replace(/^0+/, ''),
        largo: [10, 11],
        movil: null,
        motivoNoMovil: ''
    }
]

export const PAIS_POR_DEFECTO = '595'

export const buscarPais = (codigo) =>
    PAISES.find((p) => p.codigo === codigo) || PAISES[0]

// Un número guardado como `+595...` ya trae el país. Se detecta SÓLO con el
// `+` adelante, nunca a partir de los dígitos pelados: los códigos de país
// chocan con códigos de área reales (el DDD 54 en Brasil es Caxias do Sul),
// así que un `5491234...` tipeado con Brasil elegido se leería como Argentina.
// Con `+` la intención es inequívoca; sin `+`, manda el país elegido.
export function separarPais(valor) {
    const crudo = String(valor || '').trim()
    const digitos = crudo.replace(/\D/g, '')
    if (!digitos) return { pais: null, resto: '' }
    if (!crudo.startsWith('+')) return { pais: null, resto: digitos }

    // Del más largo al más corto: `595` antes que `55`/`54`.
    const ordenados = [...PAISES].sort((a, b) => b.codigo.length - a.codigo.length)
    const pais = ordenados.find((p) => digitos.startsWith(p.codigo))
    return pais
        ? { pais, resto: digitos.slice(pais.codigo.length) }
        : { pais: null, resto: digitos }
}

// País efectivo + parte nacional normalizada + veredicto.
//
// `motivo` no es decorativo: abrir WhatsApp sin destinatario válido es un
// fallo silencioso —el operador cree que avisó y el cliente nunca recibió
// nada— así que cuando no se puede armar el link hay que poder decir por qué.
export function resolverNumero(valor, paisCodigo = PAIS_POR_DEFECTO) {
    const elegido = buscarPais(paisCodigo)
    const { pais, resto } = separarPais(valor)

    let efectivo = pais || elegido
    let nacional

    if (pais) {
        nacional = pais.local(resto)
    } else {
        // Sin `+`: manda el país elegido. Pero si lo pegado ya empieza con el
        // código de ESE país y lo que sobra tiene largo válido, se asume que
        // ya venía con código y no se duplica (`595981000111` ->
        // `595981000111`, no `595595981000111`). Acotado al país elegido y con
        // chequeo de largo: un DDD brasileño 55 (`55912345678`, sobran 9) no
        // pasa el largo y se trata como nacional, que es lo correcto.
        const [min, max] = elegido.largo
        const sinCodigo = resto.startsWith(elegido.codigo)
            ? resto.slice(elegido.codigo.length)
            : null
        nacional = sinCodigo !== null && sinCodigo.length >= min && sinCodigo.length <= max
            ? elegido.local(sinCodigo)
            : elegido.local(resto)
    }

    const internacional = nacional ? `${efectivo.codigo}${nacional}` : ''
    const [min, max] = efectivo.largo

    if (!nacional) {
        return { pais: efectivo, nacional, internacional, valido: false, motivo: '' }
    }
    if (nacional.length < min || nacional.length > max) {
        return {
            pais: efectivo,
            nacional,
            internacional,
            valido: false,
            motivo: `Número incompleto para ${efectivo.nombre}.`
        }
    }
    if (efectivo.movil && !efectivo.movil.test(nacional)) {
        return {
            pais: efectivo,
            nacional,
            internacional,
            valido: false,
            motivo: efectivo.motivoNoMovil
        }
    }
    return { pais: efectivo, nacional, internacional, valido: true, motivo: '' }
}

export function linkWhatsapp(internacional, mensaje) {
    return `https://wa.me/${internacional}?text=${encodeURIComponent(mensaje)}`
}
